import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { toast } from "react-toastify";
import moment from "moment";
import { Avatar, Spinner } from "../../components/lawyer/LawyerUi";
import { Attachment, fileNameOf, fileTypeOf, mergeMessages } from "./ChatConversation";
import socketService from "../../services/SocketService";

// The "Legal Platform Support" chat with the admin team. Shared by the user and
// lawyer portals: `side` is "user" or "lawyer", `api` is userService/lawyerService.

export const SUPPORT_NAME = "Support Chat";
export const SUPPORT_CHAT_ID = "support";

export const SupportAvatar = ({ size = 60, className = "" }) => (
  <span className={`lwp-avatar-fallback lwp-support-avatar ${className}`} style={{ width: size, height: size }} aria-label={SUPPORT_NAME}>
    <i className="bi bi-headset" style={{ fontSize: size * 0.48 }} />
  </span>
);

const listTime = (value) => {
  const m = moment(value);
  if (!m.isValid()) return "";
  if (m.isSame(moment(), "day")) return m.format("hh:mm A");
  if (m.isSame(moment().subtract(1, "day"), "day")) return "Yesterday";
  return m.format("DD MMM");
};

const previewText = (msg) => {
  if (!msg) return "Message us anytime";
  const label = { image: "Photo", video: "Video", audio: "Voice message", doc: "Document" }[msg.file_type];
  return label || msg.message || "";
};

// Pinned row at the top of the chat list.
export const SupportChatItem = ({ api, side, basePath, active }) => {
  const [chat, setChat] = useState(null);

  const load = useCallback(() => {
    api
      .getSupportChat()
      .then((res) => res?.data?.status && setChat(res.data.data.chat))
      .catch(() => {});
  }, [api]);

  useEffect(load, [load]);

  useEffect(
    () =>
      socketService.subscribe("receive-support-message", (message) => {
        if (message?.sender !== "admin") return;
        setChat((prev) => ({
          ...(prev || {}),
          last_message: message,
          unread_count: active ? 0 : (prev?.unread_count || 0) + 1,
        }));
      }),
    [active]
  );

  // Opening the conversation marks it read on the server; mirror that locally.
  useEffect(() => {
    if (active) setChat((prev) => (prev ? { ...prev, unread_count: 0 } : prev));
  }, [active]);

  // The open conversation reports its own sends so the preview stays current.
  useEffect(() => {
    const onSent = (e) => setChat((prev) => ({ ...(prev || {}), last_message: e.detail }));
    window.addEventListener("lwp-support-sent", onSent);
    return () => window.removeEventListener("lwp-support-sent", onSent);
  }, []);

  const last = chat?.last_message;
  const mine = last && last.sender === side;
  return (
    <li className="lwp-support-row">
      <Link to={`${basePath}/${SUPPORT_CHAT_ID}`} className={`lwp-chat-item${active ? " active" : ""}`}>
        <SupportAvatar size={60} />
        <span className="lwp-chat-item-body">
          <span className="lwp-chat-item-row">
            <strong>
              {SUPPORT_NAME} <i className="bi bi-patch-check-fill lwp-support-badge" aria-label="Official" />
            </strong>
            <time>{listTime(last?.created_at)}</time>
          </span>
          <span className="lwp-chat-item-row">
            <span className="lwp-chat-item-preview">
              {mine ? "You: " : ""}
              {previewText(last)}
            </span>
            {chat?.unread_count > 0 && <span className="lwp-unread">{chat.unread_count}</span>}
          </span>
        </span>
      </Link>
    </li>
  );
};

const SupportConversation = ({ api, side, onBack, onActivity }) => {
  const { profile } = useOutletContext();
  const [messages, setMessages] = useState([]);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [text, setText] = useState("");
  const [pendingFile, setPendingFile] = useState(null);
  const [sending, setSending] = useState(false);
  const [chatId, setChatId] = useState(null);

  const scrollRef = useRef(null);
  const fileRef = useRef(null);
  const inputRef = useRef(null);
  const stickToBottom = useRef(true);
  const prevScrollHeight = useRef(null);
  const onActivityRef = useRef(onActivity);
  onActivityRef.current = onActivity;

  const socketChat = useCallback(() => ({ id: chatId, [`${side}_id`]: profile?.id }), [chatId, side, profile?.id]);

  const loadPage = useCallback(
    async (nextPage) => {
      const res = await api.getSupportChatMessages(nextPage);
      if (!res?.data?.status) throw new Error(res?.data?.message);
      const data = res.data.data || {};
      if (data.chat?.id) setChatId(data.chat.id);
      setMessages((prev) => mergeMessages(nextPage === 1 ? [] : prev, data.messages || []));
      setPage(data.pagination?.current_page || nextPage);
      setLastPage(data.pagination?.last_page || 1);
    },
    [api]
  );

  useEffect(() => {
    let active = true;
    stickToBottom.current = true;
    loadPage(1)
      .catch((err) => active && toast.error(err?.message || "Unable to load messages."))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [loadPage]);

  // Loading the messages marked the admin's ones read; tell the admin panel live.
  useEffect(() => {
    if (!chatId || !profile?.id) return;
    socketService.getSocket()?.emit("support-read", { reader: side }, socketChat());
    onActivityRef.current?.();
  }, [chatId, profile?.id, side, socketChat]);

  useEffect(() => {
    const offs = [
      socketService.subscribe("receive-support-message", (message) => {
        if (message?.sender !== "admin") return;
        stickToBottom.current = true;
        setMessages((prev) => mergeMessages(prev, [message]));
        api.readSupportMessages().catch(() => {});
        socketService.getSocket()?.emit("support-read", { reader: side }, socketChat());
      }),
      socketService.subscribe("support-read", (data) => {
        if (data?.reader === "admin") {
          setMessages((prev) => prev.map((m) => (m.sender === side ? { ...m, is_read: 1 } : m)));
        }
      }),
    ];
    return () => offs.forEach((off) => off());
  }, [api, side, socketChat]);

  const loadOlder = async () => {
    if (loadingOlder || page >= lastPage) return;
    setLoadingOlder(true);
    prevScrollHeight.current = scrollRef.current?.scrollHeight || null;
    try {
      await loadPage(page + 1);
    } catch (err) {
      toast.error(err?.message || "Unable to load messages.");
    } finally {
      setLoadingOlder(false);
    }
  };

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (prevScrollHeight.current !== null) {
      el.scrollTop = el.scrollHeight - prevScrollHeight.current;
      prevScrollHeight.current = null;
    } else if (stickToBottom.current) {
      el.scrollTop = el.scrollHeight;
    }
  }, [messages]);

  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    stickToBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    if (el.scrollTop < 60) loadOlder();
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    const body = text.trim();
    if (sending || (!body && !pendingFile)) return;
    setSending(true);
    try {
      const payload = pendingFile
        ? { file: pendingFile, file_type: fileTypeOf(pendingFile), message: body || pendingFile.name.replace(/\s+/g, "") }
        : { message: body };
      const res = await api.sendSupportMessage(payload);
      if (!res?.data?.status) throw new Error(res?.data?.message);
      const message = res.data.data;
      stickToBottom.current = true;
      setMessages((prev) => mergeMessages(prev, [message]));
      const chat = { id: message.support_chat_id, [`${side}_id`]: profile?.id };
      if (!chatId) setChatId(message.support_chat_id);
      socketService.getSocket()?.emit("support-message", message, chat);
      window.dispatchEvent(new CustomEvent("lwp-support-sent", { detail: message }));
      setText("");
      setPendingFile(null);
      inputRef.current?.focus();
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Message not sent.");
    } finally {
      setSending(false);
    }
  };

  const pickFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) {
      toast.error("Files must be 20 MB or smaller.");
      return;
    }
    setPendingFile(file);
  };

  let lastDay = null;
  const canSend = !!text.trim() || !!pendingFile;

  return (
    <div className="lwp-convo">
      <header className="lwp-convo-head">
        <button type="button" className="lwp-back lwp-convo-back" onClick={onBack} aria-label="Back to chats">
          <i className="bi bi-chevron-left" />
        </button>
        <SupportAvatar size={44} className="lwp-convo-avatar" />
        <div className="lwp-convo-title">
          <h2>
            {SUPPORT_NAME} <i className="bi bi-patch-check-fill lwp-support-badge" aria-label="Official" />
          </h2>
          <span>Official support team</span>
        </div>
      </header>

      <div className="lwp-convo-body" ref={scrollRef} onScroll={onScroll}>
        {loading ? (
          <Spinner />
        ) : (
          <>
            {loadingOlder && <Spinner />}
            {messages.length === 0 && <p className="lwp-convo-hint">Need help? Send a message and our team will reply here.</p>}
            {messages.map((m) => {
              const day = moment(m.created_at).format("YYYY-MM-DD");
              const divider = day !== lastDay;
              lastDay = day;
              const mine = m.sender === side;
              const body = (m.message || "").trim();
              const showText = body && !(m.file && body === fileNameOf(m.file));
              const time = moment(m.created_at);
              return (
                <React.Fragment key={m.id}>
                  {divider && (
                    <div className="lwp-day-divider">
                      <span>{moment(m.created_at).format("DD MMM, YYYY")}</span>
                    </div>
                  )}
                  <div className={`lwp-msg${mine ? " mine" : ""}`} id={`lwp-msg-${m.id}`}>
                    {!mine && <SupportAvatar size={44} className="lwp-msg-avatar" />}
                    <div className="lwp-msg-col">
                      <div className="lwp-msg-line">
                        <div className={`lwp-bubble${m.file_type === "image" || m.file_type === "video" ? " has-media" : ""}`}>
                          <Attachment message={m} />
                          {showText && <p className="lwp-bubble-text">{body}</p>}
                        </div>
                      </div>
                      <span className="lwp-msg-meta">
                        {mine && (
                          <i
                            className={`bi ${Number(m.is_read) === 1 ? "bi-check2-all read" : "bi-check2"}`}
                            aria-label={Number(m.is_read) === 1 ? "Read" : "Sent"}
                          />
                        )}
                        {time.isValid() ? time.format("hh:mm A") : ""}
                      </span>
                    </div>
                    {mine && <Avatar src={profile?.picture} size={44} className="lwp-msg-avatar" />}
                  </div>
                </React.Fragment>
              );
            })}
          </>
        )}
      </div>

      <form className="lwp-composer" onSubmit={handleSubmit}>
        {pendingFile && (
          <div className="lwp-pending-file">
            <i className={`bi ${fileTypeOf(pendingFile) === "image" ? "bi-image" : "bi-paperclip"}`} />
            <span>{pendingFile.name}</span>
            <button type="button" className="lwp-icon-btn" onClick={() => setPendingFile(null)} aria-label="Remove file">
              <i className="bi bi-x-lg" />
            </button>
          </div>
        )}
        <div className="lwp-composer-bar">
          <button
            type="button"
            className="lwp-icon-btn lwp-composer-icon"
            onClick={() => fileRef.current?.click()}
            aria-label="Attach file"
            disabled={sending}
          >
            <i className="bi bi-paperclip" />
          </button>
          <input ref={fileRef} type="file" hidden onChange={pickFile} />
          <textarea
            ref={inputRef}
            rows={1}
            className="lwp-composer-input"
            placeholder="Type a message..."
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSubmit();
              }
            }}
          />
          <button type="submit" className="lwp-send-btn" disabled={sending || !canSend} aria-label="Send">
            {sending ? <span className="spinner-border spinner-border-sm" /> : <i className="bi bi-send-fill" />}
          </button>
        </div>
      </form>
    </div>
  );
};

export default SupportConversation;
